import { useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';
import r2 from '@data/r2-assets.json';
import { useT } from '@lib/i18n';
import type { Unit } from '@lib/types';

interface FloorPlanImageProps {
  unit: Unit;
  size?: 'thumb' | 'large';
  className?: string;
}

type LoadStage = 'r2' | 'local' | 'placeholder';

const LOCAL_FALLBACK: Record<string, string> = r2.floorPlans.fallback_local;

/**
 * 3-tier loader for unit floor plans:
 *   1. R2 CDN (per documented pattern in r2-assets.json)
 *   2. Local placeholder file in /public/floorplans/{type}.jpg if present
 *   3. Styled in-card placeholder (icon + type label) — always works
 *
 * On unit change, restart from tier 1.
 */
export default function FloorPlanImage({
  unit,
  size = 'thumb',
  className,
}: FloorPlanImageProps) {
  const t = useT();
  const [stage, setStage] = useState<LoadStage>('r2');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setStage('r2');
    setLoaded(false);
  }, [unit.unitCode]);

  const aspect = size === 'large' ? 'aspect-[4/3]' : 'aspect-[4/3]';
  const wrapper = `relative w-full ${aspect} bg-navy-50 overflow-hidden ${className ?? ''}`;

  if (stage === 'placeholder') {
    return (
      <div className={wrapper}>
        <PlaceholderContent unit={unit} t={t} />
      </div>
    );
  }

  const src =
    stage === 'r2' ? buildR2Url(unit) : LOCAL_FALLBACK[unit.type] ?? '';

  return (
    <div className={wrapper}>
      {!loaded && <Skeleton />}
      <img
        key={`${unit.unitCode}-${stage}`}
        src={src}
        alt={`${t('match.floorPlanAlt')} ${unit.unitCode}`}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(false);
          setStage((s) => (s === 'r2' ? 'local' : 'placeholder'));
        }}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
}

function buildR2Url(unit: Unit): string {
  // Pattern: {baseUrl}/key2/floor-plans/{unitCode}.jpg
  return `${r2.baseUrl}/key2/floor-plans/${encodeURIComponent(unit.unitCode)}.jpg`;
}

function Skeleton() {
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-navy-50 to-navy-100 animate-pulse" />
  );
}

function PlaceholderContent({
  unit,
  t,
}: {
  unit: Unit;
  t: (k: 'match.imgUnavailable') => string;
}) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-navy-50 to-gold-50/40 text-navy-400">
      <ImageOff className="w-8 h-8" strokeWidth={1.5} />
      <div className="text-xs">{t('match.imgUnavailable')}</div>
      <div className="text-base font-bold text-navy num-en">{unit.type}</div>
    </div>
  );
}
